import { Component, computed, inject, input } from '@angular/core';

import { Router } from '@angular/router';
import {
  NavigationCardComponent,
  NavigationCard,
} from './navigation-card.component';
import { TicketingNavigationCardComponent } from './ticketing-navigation-card.component';
import { AuthStore } from '../../../store/auth.store';
import {
  ServicePermissionUtils,
  SERVICE_KEY,
  PERMISSIONS,
} from '@sandbox-platform/shared-ui';

@Component({
  standalone: true,
  imports: [NavigationCardComponent, TicketingNavigationCardComponent],
  selector: 'lib-navigation-grid',
  template: `
    <!-- Welcome Message -->
    <div class="text-center mb-12 flex flex-col gap-4">
      <div class="flex flex-col gap-y-2 items-center">
        <h2 class="text-4xl font-extrabold text-gray-700">
          Welcome back, {{ firstName() }}!
        </h2>
        @if (authStore.currentCompany()) {

        <span class="text-gray-700 text-sm"
          >You are logged into
          <b>{{ authStore.currentCompany()?.name }}</b></span
        >
        }
      </div>
    </div>

    <!-- Navigation Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (card of navigationCards(); track card.route) {
      <app-navigation-card
        [title]="card.title"
        [description]="card.description"
        [route]="card.route"
        [iconPath]="card.iconPath"
        (navigate)="navigateTo($event)"
      />
      } @if (hasTicketingAccess()) {
      <app-ticketing-navigation-card />
      }
    </div>
  `,
})
export class NavigationGridComponent {
  private router = inject(Router);
  public authStore = inject(AuthStore);

  // Input for first name
  firstName = input.required<string>();

  private readonly moduleDefinitions: Array<
    Omit<NavigationCard, 'isEnabled'> & {
      serviceKey: string;
      requiredPermission: string;
    }
  > = [
    {
      title: 'Administrative Tasks',
      description:
        'Manage system settings, users, and organizational configurations',
      route: '/admin',
      iconPath:
        'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
      serviceKey: SERVICE_KEY.ADMIN,
      requiredPermission: PERMISSIONS.ADMIN.USERS_READ,
    },
    {
      title: 'Expert Management',
      description:
        'Oversee expert profiles, assignments, and performance metrics+',
      route: '/expert',
      iconPath:
        'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
      serviceKey: SERVICE_KEY.EXPERT,
      requiredPermission: PERMISSIONS.EXPERT.DASHBOARD_READ,
    },
    {
      title: 'Audit Console',
      description:
        'Review system activity, trace user actions, and inspect operational events',
      route: '/audit',
      iconPath:
        'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      serviceKey: SERVICE_KEY.AUDIT,
      requiredPermission: PERMISSIONS.AUDIT.AUDIT_LOGS_READ,
    },
  ];

  readonly navigationCards = computed<NavigationCard[]>(() => {
    const user = this.authStore.user();

    return this.moduleDefinitions
      .map(({ serviceKey, requiredPermission, ...cardProps }) => ({
        ...cardProps,
        isEnabled: ServicePermissionUtils.hasPermission(
          user,
          serviceKey,
          requiredPermission
        ),
      }))
      .filter((card) => card.isEnabled);
  });

  readonly hasTicketingAccess = computed<boolean>(() => {
    const user = this.authStore.user();
    return (
      ServicePermissionUtils.hasPermission(
        user,
        SERVICE_KEY.TICKETING,
        PERMISSIONS.TICKETING.TICKETS_READ
      ) ||
      ServicePermissionUtils.hasPermission(
        user,
        SERVICE_KEY.TICKETING,
        PERMISSIONS.TICKETING.ASSIGNMENTS_READ
      ) ||
      ServicePermissionUtils.hasPermission(
        user,
        SERVICE_KEY.TICKETING,
        PERMISSIONS.TICKETING.CATEGORIES_READ
      )
    );
  });

  // Navigation method
  navigateTo(route: string): void {
    if (route === '/admin') {
      window.location.href = '/admin/dashboard';
    } else if (route === '/expert') {
      window.location.href = '/expert/dashboard';
    } else if (route === '/audit') {
      window.location.href = '/audit/audit-logs';
    } else if (route === '/licensing') {
      window.location.href = '/licensing/dashboard';
    } else {
      this.router.navigate([route]);
    }
  }
}
